import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from './useFetch';
import type { ILegalTransactionFullObject } from '../Interfaces/ILegalTransaction';
import type { ILegalTransactionProgress } from '../Interfaces/ILegalTransactionProgress';
import type { IDocument } from '../Interfaces/IDocument';
import { GenderSchema, type IGender } from '../Interfaces/IGender';
import {
  AcademicTitleSchema,
  type IAcademicTitle,
} from '../Interfaces/IAcademicTitle';
import { CountrySchema, type ICountry } from '../Interfaces/ICountry';
import { MIMETypeSchema, type IMIMEType } from '../Interfaces/IMIMEType';
import { ContactSchema, type IContact } from '../Interfaces/IContact';
import type { IDraftMessage } from '../Interfaces/IDraftMessage';
import type { IContactInvite } from '../Interfaces/IContactInvite';
import type { ISystem } from '../Interfaces/ISystem';
import {
  bookingOptionsResponseSchema,
  freeBusyResponseSchema,
  meetingRequestsResponseSchema,
  type IBookingOption,
  type ICreateMeetingRequestPayload,
  type IFreeBusySlot,
  type IMeetingRequest,
} from '../Interfaces/IMeetingRequest';
import { ConfigSchema, type IConfig } from '../Interfaces/IConfig';
import type {
  ILegalTransactionMetadata,
  ILegalTransactionsMetadata,
} from '../Interfaces/ILegalTransactionMetadata';
import type { ILegalTransactionCustomId } from '../Interfaces/ILegalTransactionCustomId';
import {
  InboxMailListItemSchema,
  InboxMailSchema,
  type IInboxMail,
  type IInboxMailListItem,
} from '../Interfaces/IInboxMail';
import {
  AccountTrackingListItemSchema,
  AccountTrackingSchema,
  type IAccountTracking,
  type IAccountTrackingListItem,
} from '../Interfaces/IAccountTracking';
import { z } from 'zod';
import {
  addOptimistic,
  makeOptimisticId,
  type OptimisticBase,
} from './useOptimistic';

/** Optimistische Terminanfrage (vor dem Server-Sync). */
export type IOptimisticMeetingRequest = IMeetingRequest & OptimisticBase;

const inboxMailListSchema = z.array(InboxMailListItemSchema);
const accountTrackingListSchema = z.array(AccountTrackingListItemSchema);

export const useLegalTransactions = () => {
  const {
    data: legalTransactions,
    isLoading: isLoadingLegalTransactions,
    refetch: refetchLegalTransactions,
  } = useQuery<ILegalTransactionFullObject[]>({
    queryKey: ['legalTransactions'],
    queryFn: () => customFetcher({ endpoint: '/legaltransaction' }),
    refetchInterval: 30_000,
  });

  return {
    legalTransactions,
    isLoadingLegalTransactions,
    refetchLegalTransactions,
  };
};

export const useLegalTransactionProgresses = () => {
  const {
    data: legalTransactionProgresses,
    isLoading: isLoadingLegalTransactionsProgresses,
    refetch: refetchLegalTransactionsProgresses,
  } = useQuery<ILegalTransactionProgress[]>({
    queryKey: ['legalTransactionsProgresses'],
    queryFn: () => customFetcher({ endpoint: '/legaltransactionprogress' }),
  });

  return {
    legalTransactionProgresses,
    isLoadingLegalTransactionsProgresses,
    refetchLegalTransactionsProgresses,
  };
};

export const useDocuments = () => {
  const { data: documents, isLoading: isLoadingDocuments, refetch: refetchDocuments } = useQuery<
    IDocument[]
  >({
    queryKey: ['documents'],
    queryFn: () => customFetcher({ endpoint: '/document' }),
  });

  return {
    documents,
    isLoadingDocuments,
    refetchDocuments,
  };
};

/**
 * Lädt ein einzelnes Dokument inkl. base64-Daten gezielt nach (für den Download).
 * Die Liste aus {@link useDocuments} enthält bewusst keine Daten mehr, damit beim
 * Öffnen eines Vorgangs nicht alle Dateien geladen werden. Erst beim Klick auf
 * "Herunterladen" wird die jeweilige Datei über diesen Aufruf vom Backend geholt.
 */
export const fetchDocumentData = (idDocument: number) =>
  customFetcher({ endpoint: `/document/${idDocument}` }) as Promise<IDocument>;

const genderListSchema = z.array(GenderSchema);
const academicTitleListSchema = z.array(AcademicTitleSchema);
const countryListSchema = z.array(CountrySchema);
const mimeTypeListSchema = z.array(MIMETypeSchema);

export const useGenders = () => {
  const { data: genders, isLoading: isLoadingGenders } = useQuery<IGender[]>({
    queryKey: ['genders'],
    queryFn: () =>
      customFetcher({ endpoint: '/gender', schema: genderListSchema }),
  });

  return {
    genders,
    isLoadingGenders,
  };
};

export const useAcademicTitles = () => {
  const { data: academicTitles, isLoading: isLoadingAcademicTitles } = useQuery<
    IAcademicTitle[]
  >({
    queryKey: ['academicTitles'],
    queryFn: () =>
      customFetcher({
        endpoint: '/academictitle',
        schema: academicTitleListSchema,
      }),
  });

  return {
    academicTitles,
    isLoadingAcademicTitles,
  };
};

export const useCountries = () => {
  const { data: countries, isLoading: isLoadingCountries } = useQuery<
    ICountry[]
  >({
    queryKey: ['countries'],
    queryFn: () =>
      customFetcher({ endpoint: '/country', schema: countryListSchema }),
  });

  return {
    countries,
    isLoadingCountries,
  };
};

export const useMimeTypes = () => {
  const { data: mimeTypes, isLoading: isLoadingMimeTypes } = useQuery<
    IMIMEType[]
  >({
    queryKey: ['mimeTypes'],
    queryFn: () =>
      customFetcher({ endpoint: '/mimetype', schema: mimeTypeListSchema }),
  });

  return {
    mimeTypes,
    isLoadingMimeTypes,
  };
};

export const useContact = () => {
  const { data: contact, isLoading: isLoadingContact } = useQuery<IContact>({
    queryKey: ['contact'],
    queryFn: () =>
      customFetcher({ endpoint: '/contact', schema: ContactSchema }),
  });

  return {
    contact,
    isLoadingContact,
  };
};

export const useUpdateContact = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: updateContact, isPending: isPendingUpdateContact } =
    useMutation({
      mutationFn: (contact: IContact) =>
        customFetcher({ endpoint: '/contact', method: 'PUT', body: contact }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['contact'] });
      },
    });

  return {
    updateContact,
    isPendingUpdateContact,
  };
};

export const useCreateDraftMessage = () => {
  const {
    mutateAsync: createDraftMessage,
    isPending: isPendingCreateDraftMessage,
  } = useMutation({
    mutationFn: (draftMessage: IDraftMessage) =>
      customFetcher({
        endpoint: '/draftmessage',
        method: 'POST',
        body: draftMessage,
      }),
  });

  return {
    createDraftMessage,
    isPendingCreateDraftMessage,
  };
};

export const useCreateDocumentDownloaded = () => {
  const {
    mutateAsync: createDocumentDownloaded,
    isPending: isPendingDocumentDownloaded,
  } = useMutation({
    mutationFn: (document: IDocument) =>
      customFetcher({
        endpoint: '/document',
        method: 'POST',
        body: document,
      }),
  });

  return {
    createDocumentDownloaded,
    isPendingDocumentDownloaded,
  };
};

export const useCreateContactInvite = () => {
  const {
    mutateAsync: createContactInvite,
    isPending: isPendingCreateContactInvite,
  } = useMutation({
    mutationFn: (contactInvite: IContactInvite) =>
      customFetcher({
        endpoint: '/contactinvite/invite',
        method: 'POST',
        body: contactInvite,
      }),
  });

  return {
    createContactInvite,
    isPendingCreateContactInvite,
  };
};

export const useRequestNewPassword = () => {
  const {
    mutateAsync: requestNewPassword,
    isPending: isPendingRequestNewPassword,
  } = useMutation({
    mutationFn: (email: string) =>
      customFetcher({
        endpoint: '/contact/requestNewPassword',
        method: 'POST',
        body: { Email: email },
      }),
  });

  return {
    requestNewPassword,
    isPendingRequestNewPassword,
  };
};

export const useValidatePassword = () => {
  const {
    mutateAsync: validatePassword,
    isPending: isPendingValidatePassword,
  } = useMutation({
    mutationFn: (password: string) =>
      customFetcher({
        endpoint: '/contact/validatePassword',
        method: 'POST',
        body: { Password: password },
      }),
  });

  return {
    validatePassword,
    isPendingValidatePassword,
  };
};

export const useCreateSystem = () => {
  const { mutateAsync: createSystem, isPending: isPendingCreateSystem } =
    useMutation({
      mutationFn: (system: ISystem) =>
        customFetcher({
          endpoint: '/system',
          method: 'POST',
          body: system,
        }),
    });

  return {
    createSystem,
    isPendingCreateSystem,
  };
};

export const useConfig = () => {
  const { data: config, isLoading: isLoadingConfig } = useQuery<IConfig>({
    queryKey: ['config'],
    queryFn: () =>
      customFetcher({ endpoint: '/config', schema: ConfigSchema }),
  });

  return {
    config,
    isLoadingConfig,
  };
};

export const useMetadata = () => {
  const {
    data: metadata,
    isLoading: isLoadingMetadata,
    refetch: refetchMetadata,
  } = useQuery<ILegalTransactionsMetadata>({
    queryKey: ['metadata'],
    queryFn: () => customFetcher({ endpoint: '/legaltransaction/getMetadata' }),
  });

  return {
    metadata,
    isLoadingMetadata,
    refetchMetadata,
  };
};

export const useUpdateMetadata = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: updateMetadata, isPending: isPendingUpdateMetadata } =
    useMutation({
      mutationFn: (metadata: ILegalTransactionMetadata) =>
        customFetcher({
          endpoint: '/legaltransaction/updateMetadata',
          method: 'PUT',
          body: metadata,
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['metadata'] });
      },
    });

  return {
    updateMetadata,
    isPendingUpdateMetadata,
  };
};

export const useCreateCustomId = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: createCustomId, isPending: isPendingCreateCustomId } =
    useMutation({
      mutationFn: (customId: ILegalTransactionCustomId) =>
        customFetcher({
          endpoint: '/legaltransaction/createCustomId',
          method: 'POST',
          body: customId,
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['legalTransactions'] });
      },
    });

  return {
    createCustomId,
    isPendingCreateCustomId,
  };
};

export const useUpdateCustomId = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: updateCustomId, isPending: isPendingUpdateCustomId } =
    useMutation({
      mutationFn: (customId: ILegalTransactionCustomId) =>
        customFetcher({
          endpoint: '/legaltransaction/updateCustomId',
          method: 'PUT',
          body: customId,
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['legalTransactions'] });
      },
    });

  return {
    updateCustomId,
    isPendingUpdateCustomId,
  };
};

export const useDeleteCustomId = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: deleteCustomId, isPending: isPendingDeleteCustomId } =
    useMutation({
      mutationFn: (customId: Omit<ILegalTransactionCustomId, 'customId'>) =>
        customFetcher({
          endpoint: '/legaltransaction/deleteCustomId',
          method: 'DELETE',
          body: customId,
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['legalTransactions'] });
      },
    });

  return {
    deleteCustomId,
    isPendingDeleteCustomId,
  };
};

export const useInboxMails = () => {
  const {
    data: inboxMails,
    isLoading: isLoadingInboxMails,
    refetch: refetchInboxMails,
  } = useQuery<IInboxMailListItem[]>({
    queryKey: ['inbox'],
    queryFn: () =>
      customFetcher({ endpoint: '/inbox', schema: inboxMailListSchema }),
    refetchInterval: 60_000,
  });

  return { inboxMails, isLoadingInboxMails, refetchInboxMails };
};

export const useInboxMail = (idMail: number | null | undefined) => {
  const {
    data: inboxMail,
    isLoading: isLoadingInboxMail,
    refetch: refetchInboxMail,
  } = useQuery<IInboxMail>({
    queryKey: ['inboxMail', idMail],
    queryFn: () =>
      customFetcher({
        endpoint: `/inbox/${idMail}`,
        schema: InboxMailSchema,
      }),
    enabled: idMail !== null && idMail !== undefined,
  });

  return { inboxMail, isLoadingInboxMail, refetchInboxMail };
};

export const useMarkInboxMailRead = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: markInboxMailRead } = useMutation({
    mutationFn: (idMail: number) =>
      customFetcher({
        endpoint: `/inbox/${idMail}/read`,
        method: 'POST',
      }),
    onSuccess: (_data, idMail) => {
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
      queryClient.invalidateQueries({ queryKey: ['inboxMail', idMail] });
    },
  });
  return { markInboxMailRead };
};

export const useRequestInboxAttachment = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: requestInboxAttachment } = useMutation({
    mutationFn: ({ idMail, idDocument }: { idMail: number; idDocument: number }) =>
      customFetcher({
        endpoint: `/inbox/${idMail}/attachments/${idDocument}/request`,
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
  return { requestInboxAttachment };
};

export const useReplyToInboxMail = () => {
  const { mutateAsync: replyToInboxMail, isPending: isReplying } = useMutation({
    mutationFn: ({ idMail, body }: { idMail: number; body: string }) =>
      customFetcher({
        endpoint: `/inbox/${idMail}/reply`,
        method: 'POST',
        body: { Body: body },
      }),
  });
  return { replyToInboxMail, isReplying };
};

// --- Notaranderkonto-Nachverfolgung (Mandantenportal) ---------------------
export const useAccountTrackings = (
  idLegalTransaction: number | null | undefined
) => {
  const {
    data: accountTrackings,
    isLoading: isLoadingAccountTrackings,
    refetch: refetchAccountTrackings,
  } = useQuery<IAccountTrackingListItem[]>({
    queryKey: ['accountTracking', idLegalTransaction],
    queryFn: () =>
      customFetcher({
        endpoint: `/accounttracking?idLegalTransaction=${idLegalTransaction}`,
        schema: accountTrackingListSchema,
      }),
    enabled: idLegalTransaction !== null && idLegalTransaction !== undefined,
    refetchInterval: 60_000,
  });

  return { accountTrackings, isLoadingAccountTrackings, refetchAccountTrackings };
};

export const useAccountTracking = (
  idTracking: number | null | undefined
) => {
  const {
    data: accountTracking,
    isLoading: isLoadingAccountTracking,
    refetch: refetchAccountTracking,
  } = useQuery<IAccountTracking>({
    queryKey: ['accountTrackingDetail', idTracking],
    queryFn: () =>
      customFetcher({
        endpoint: `/accounttracking/${idTracking}`,
        schema: AccountTrackingSchema,
      }),
    enabled: idTracking !== null && idTracking !== undefined,
  });

  return { accountTracking, isLoadingAccountTracking, refetchAccountTracking };
};

// ---------------------------------------------------------------------------
// Mandanten-Terminanfragen
// ---------------------------------------------------------------------------

/** Eigene Terminanfragen des Mandanten zu einem Vorgang. */
export const useMeetingRequests = (
  idLegalTransaction: number | null | undefined
) => {
  const {
    data,
    isLoading: isLoadingMeetingRequests,
    refetch: refetchMeetingRequests,
  } = useQuery({
    queryKey: ['meetingRequests', idLegalTransaction],
    queryFn: () =>
      customFetcher({
        endpoint: `/legaltransaction/${idLegalTransaction}/meetingrequest`,
        schema: meetingRequestsResponseSchema,
      }),
    enabled: idLegalTransaction !== null && idLegalTransaction !== undefined,
    refetchInterval: 60_000,
  });

  const meetingRequests: IMeetingRequest[] = data?.requests ?? [];

  return { meetingRequests, isLoadingMeetingRequests, refetchMeetingRequests };
};

/** Mandant bestätigt/lehnt einen Gegenvorschlag ab. */
export const useCounterDecision = (
  idLegalTransaction: number | null | undefined
) => {
  const queryClient = useQueryClient();
  const { mutateAsync: sendCounterDecision, isPending: isSendingCounterDecision } =
    useMutation({
      mutationFn: ({
        idLegalTransactionMeetingRequest,
        decision,
        clientMessage,
      }: {
        idLegalTransactionMeetingRequest: number;
        decision: 'accept' | 'reject';
        clientMessage?: string;
      }) =>
        customFetcher({
          endpoint: `/legaltransaction/${idLegalTransaction}/meetingrequest/${idLegalTransactionMeetingRequest}/counter-decision`,
          method: 'POST',
          body: { decision, clientMessage },
        }),
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: ['meetingRequests', idLegalTransaction],
        });
      },
    });

  return { sendCounterDecision, isSendingCounterDecision };
};

/** Öffentlich buchbare Terminarten des Vorgangs. */
export const useBookingOptions = (
  idLegalTransaction: number | null | undefined
) => {
  const { data, isLoading: isLoadingBookingOptions } = useQuery({
    queryKey: ['meetingBookingOptions', idLegalTransaction],
    queryFn: () =>
      customFetcher({
        endpoint: `/legaltransaction/${idLegalTransaction}/bookingoptions`,
        schema: bookingOptionsResponseSchema,
      }),
    enabled: idLegalTransaction !== null && idLegalTransaction !== undefined,
  });

  const bookingOptions: IBookingOption[] = data?.meetingTypes ?? [];

  return { bookingOptions, isLoadingBookingOptions };
};

/** Freie Slots für eine Terminart (fertige Slots, keine internen Daten). */
export const useFreeBusySlots = (
  idLegalTransaction: number | null | undefined,
  idMeetingType: number | null | undefined,
  dateFrom: string,
  dateTo: string,
  idLocation?: number | null
) => {
  const { data, isLoading: isLoadingFreeBusySlots } = useQuery({
    queryKey: [
      'meetingFreeBusy',
      idLegalTransaction,
      idMeetingType,
      idLocation ?? null,
      dateFrom,
      dateTo,
    ],
    queryFn: () => {
      const locationParam =
        idLocation !== null && idLocation !== undefined
          ? `&idLocation=${idLocation}`
          : '';
      return customFetcher({
        endpoint: `/legaltransaction/${idLegalTransaction}/freebusy?idMeetingType=${idMeetingType}${locationParam}&dateFrom=${dateFrom}&dateTo=${dateTo}`,
        schema: freeBusyResponseSchema,
      });
    },
    enabled:
      idLegalTransaction !== null &&
      idLegalTransaction !== undefined &&
      idMeetingType !== null &&
      idMeetingType !== undefined,
  });

  const freeBusySlots: IFreeBusySlot[] = data?.slots ?? [];

  return { freeBusySlots, isLoadingFreeBusySlots };
};

/** Neue Terminanfrage erstellen. */
export const useCreateMeetingRequest = (
  idLegalTransaction: number | null | undefined
) => {
  const queryClient = useQueryClient();
  const { mutateAsync: createMeetingRequest, isPending: isCreatingMeetingRequest } =
    useMutation({
      mutationFn: (payload: ICreateMeetingRequestPayload) =>
        customFetcher({
          endpoint: `/legaltransaction/${idLegalTransaction}/meetingrequest`,
          method: 'POST',
          body: payload,
        }),
      onSuccess: (_data, payload) => {
        // Optimistische Sofort-Anzeige: die neue Anfrage in den localStorage
        // schreiben, damit sie sofort in der Status-Liste erscheint (nicht erst
        // nach dem Server-Roundtrip). Wird dort entfernt, sobald der echte
        // Eintrag eintrifft.
        if (idLegalTransaction) {
          addOptimistic<IOptimisticMeetingRequest>(
            `dino:opt:meetingRequests:${idLegalTransaction}`,
            {
              _optimisticId: makeOptimisticId(),
              _ts: Date.now(),
              idLegalTransactionMeetingRequest: -Date.now(),
              idLegalTransaction,
              idMeetingType: payload.idMeetingType,
              RequestedStart: payload.requestedStart,
              DurationInMin: payload.durationInMin,
              Status: 10,
            }
          );
        }
        void queryClient.invalidateQueries({
          queryKey: ['meetingRequests', idLegalTransaction],
        });
        void queryClient.invalidateQueries({
          queryKey: ['meetingFreeBusy', idLegalTransaction],
        });
      },
    });

  return { createMeetingRequest, isCreatingMeetingRequest };
};
